/**
 * MessageList — presentational message-list render region for the chat stream
 * (#3277). Pure presentational: all state, refs, and handlers stay in ChatBox and
 * arrive as props; no hooks live here — ONE exception (#5263): a local
 * streaming→done ref/effect that drives the screen-reader live region below,
 * since that announcement is purely derived from this leaf's own `messages`/
 * `isStreaming` props and doesn't belong in ChatBox. Renders the
 * `chat-messages-inner` subtree and owns the messagesEndRef anchor (the outer
 * `chat-messages` div stays in ChatBox).
 *
 * CROSS-LAYER NOTE: this leaf is the locus for the `@/modules` ModuleRenderer
 * default import — features/ importing @/modules is the accepted quarantine
 * exception here; no dependency inversion is attempted.
 */
import { Fragment, memo, useEffect, useRef, useState } from 'react';
import type { RefObject, Dispatch, SetStateAction } from 'react';
import { QuickActionChips } from '@/features/chat/components/QuickActionChips';
import { resolveConsentProvider } from '@/features/oauth/components/OAuthConsentModal';
import { connectCapabilityFor } from '@/features/oauth/lib/connect-capability';
import { connectProvider } from '@/features/oauth/lib/connect-provider';
import { api } from '@/lib/api';
import { env } from '@/lib/env';
import { appendAuthToken } from '@/lib/url/appendAuthToken';
import type { ChatUIAction } from '@/lib/api/endpoints/chat';
import type { WorkspaceModule } from '@/lib/api/endpoints/workspaces';
import type { Module } from '@/types/modules';
import type { QueryClient } from '@tanstack/react-query';
import ModuleRenderer from '@/modules/_infrastructure/ModuleRenderer';
import { AssistantMarkdown } from '@/common/components/AssistantMarkdown';
import { stripInternalUUIDs } from '@/common/lib/sanitize-uuids';
import { splitResultCards } from '@/features/chat/components/result-cards/types';
import { ResultCardRenderer } from '@/features/chat/components/result-cards/ResultCardRenderer';
import { VoiceOverlay } from '@/features/voice/VoiceOverlay';
import { ErrorBoundary } from '@/common/components/ErrorBoundary';
import { parseBlocks, stripMarkdownToPlainText } from '@/features/chat/utils/assistant-content';
import { resolveImportCsvChip, isImportContactsChip } from '@/features/chat/utils/import-csv-chip';
import { RichText } from './chatbox/RichText';
import { MentionText } from './MentionText';
import { MENTION_MARKER_RE } from '@/features/chat/utils/mention-parser';
import { ThinkingTimeline } from './chatbox/ThinkingTimeline';
import { ChatInlineError } from './chatbox/ChatInlineError';
import { ModuleSelectionButtons } from './chatbox/ModuleSelectionButtons';
import type { ChatMessage, QuickAction } from '@/features/chat/hooks/useChat';

// Props mirror the identifiers the subtree reads in ChatBox. Structural values
// whose ChatBox types are complex (refs, module catalogs, voiceMode/onboarding
// hook results) are typed loosely on purpose — this is a behavior-preserving JSX
// relocation, not a type-tightening change. Module-level singletons (env, api,
// appendAuthToken, splitResultCards, parseBlocks, resolveConsentProvider) are
// imported directly rather than threaded through props.
export interface MessageListProps {
  voiceMode: any;
  messages: ChatMessage[];
  retryLastMessage: () => void | Promise<void>;
  dismissRetryBubble: () => void;
  activeConversation: { id: string } | null | undefined;
  sendMessage: (...args: any[]) => any;
  copiedMessageId: string | null;
  setCopiedMessageId: Dispatch<SetStateAction<string | null>>;
  clickableModuleLabels: any;
  openWorkspaceModuleByLabel: (label: string, messageId: string, occurrenceIndex: number) => void | Promise<void>;
  setObjectCardPopover: (v: { objectType: string; objectId: string } | null) => void;
  availableModulesRef: { current: Module[] };
  workspaceModuleCatalogRef: { current: WorkspaceModule[] };
  applyUiActions: (actions: ChatUIAction[]) => void;
  selectObject: (objectType: any, objectId: string) => void;
  onCardAction: (...args: any[]) => any;
  openModal: (...args: any[]) => any;
  archivedInlineModuleIds: Set<string>;
  setArchivedInlineModuleIds: Dispatch<SetStateAction<Set<string>>>;
  isModulePaneVisible: boolean;
  chatPaneModuleId: string | null;
  availableModules: Module[];
  setChatPaneModuleId: (id: string | null) => void;
  setIsModulePaneVisible: (v: boolean) => void;
  setModuleEmbedAnchorMessageId: (id: string | null) => void;
  token: string | null;
  workspaceId: string;
  queryClient: QueryClient;
  /** Agent-emitted follow-up chips (was onboarding.quickActions; now passed straight through). */
  quickActions: QuickAction[];
  isStreaming: boolean;
  recentlySentFilesRef: { current: Map<string, File> };
  showToast: (message: string, type?: 'success' | 'info' | 'error') => void;
  setChatImportCSVFile: (file: File | null) => void;
  confirmOAuthConsent: (provider: any) => Promise<boolean>;
  shouldShowThinkingTimeline: boolean;
  thinkingTimelineSteps: any;
  pendingModuleSelection: { modules: any[] } | null;
  handleModuleSelection: (...args: any[]) => any;
  setPendingModuleSelection: (v: any) => void;
  error: (Error & { message: string; stack?: string }) | null | undefined;
  messagesEndRef: RefObject<HTMLDivElement | null>;
}

// React.memo: composer keystrokes re-render ChatBox (inputValue state lives
// there); without the memo the whole thread re-reconciles per keystroke
// (F-performance-09, #5255). ChatBox keeps every prop identity-stable across
// unrelated renders (state/refs/useCallback/useMemo, plus useLatestCallback
// latches for the per-render-fresh handlers).
export const MessageList = memo(function MessageList({
  voiceMode,
  messages,
  retryLastMessage,
  dismissRetryBubble,
  activeConversation,
  sendMessage,
  copiedMessageId,
  setCopiedMessageId,
  clickableModuleLabels,
  openWorkspaceModuleByLabel,
  setObjectCardPopover,
  availableModulesRef,
  workspaceModuleCatalogRef,
  applyUiActions,
  selectObject,
  onCardAction,
  openModal,
  archivedInlineModuleIds,
  setArchivedInlineModuleIds,
  isModulePaneVisible,
  chatPaneModuleId,
  availableModules,
  setChatPaneModuleId,
  setIsModulePaneVisible,
  setModuleEmbedAnchorMessageId,
  token,
  workspaceId,
  queryClient,
  quickActions,
  isStreaming,
  recentlySentFilesRef,
  showToast,
  setChatImportCSVFile,
  confirmOAuthConsent,
  shouldShowThinkingTimeline,
  thinkingTimelineSteps,
  pendingModuleSelection,
  handleModuleSelection,
  setPendingModuleSelection,
  error,
  messagesEndRef,
}: MessageListProps) {
  // Screen-reader live region (#5263, F-a11y-12): streamed replies previously
  // landed in plain divs with no aria-live anywhere except the ThinkingTimeline
  // busy pill, so a screen-reader user got no signal an answer had arrived.
  // Announce on the streaming→done transition, with the last assistant turn's
  // plain text — never while streaming (that would spam per token).
  const wasStreamingRef = useRef(isStreaming);
  // Gate on message identity, not just the isStreaming edge: runChatStream
  // flips isStreaming off in a `finally` even when THIS turn appended no
  // visible assistant message (Stop pressed before any token, an
  // empty/errored turn, a silent hideAssistantResponse turn). Without this
  // gate, "last assistant message" resolves to a PRIOR turn's reply and gets
  // re-announced as if it answered the current (silent) turn. ChatMessage.id
  // is stable and freshly minted for every real reply (`assistant-<ts>`,
  // whichever code path creates it), so "id already announced" reliably
  // means "no new reply this edge" — EXCEPT the in-flight streaming bubble
  // shares that SAME id with its eventual committed message (it's appended
  // into `messages` as `streamingMessage`, then replaces itself in-place on
  // commit). runChatStream re-asserts isStreaming(true) per SSE event
  // specifically because a stray effect can clear it mid-turn — on that
  // spurious mid-turn true→false edge, `lastAssistant` still has
  // `streaming: true` (partial/empty text). Skip it (and don't record its
  // id) so the real end-of-turn edge — same id, `streaming` cleared — is
  // still free to announce the finished answer.
  const lastAnnouncedIdRef = useRef<string | null>(null);
  const announceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [liveAnnouncement, setLiveAnnouncement] = useState('');
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming) {
      const lastAssistant = [...messages].reverse().find((m) => m.role !== 'user');
      if (lastAssistant && !lastAssistant.streaming && lastAssistant.id !== lastAnnouncedIdRef.current) {
        lastAnnouncedIdRef.current = lastAssistant.id;
        if (announceTimeoutRef.current) clearTimeout(announceTimeoutRef.current);
        // Force a DOM mutation even when two consecutive replies are
        // byte-identical ("Yes.", "Done.", a repeated connect-gate message):
        // setState with an unchanged string is an Object.is no-op, so
        // aria-live would silently stay put on the second announce. Clear
        // first, then set the real text on a short delay — the same
        // clear-then-set pattern react-aria's live region uses.
        setLiveAnnouncement('');
        announceTimeoutRef.current = setTimeout(() => {
          setLiveAnnouncement(stripMarkdownToPlainText(lastAssistant.content));
          announceTimeoutRef.current = null;
        }, 100);
      }
    }
    wasStreamingRef.current = isStreaming;
  }, [isStreaming, messages]);
  // Separate unmount-only cleanup: the effect above re-runs on every
  // streaming token (messages' identity changes each delta), which would
  // otherwise clear a still-pending announce timer for unrelated dependency
  // churn if the cleanup lived in that same effect.
  useEffect(() => {
    return () => {
      if (announceTimeoutRef.current) clearTimeout(announceTimeoutRef.current);
    };
  }, []);

  return (
        <div className="chat-messages-inner">
          {voiceMode.isActive && (
            <ErrorBoundary
              onError={(error) => {
                console.error('[VoiceMode] Crash caught by ErrorBoundary:', error);
                voiceMode.exit();
              }}
            >
              <VoiceOverlay
                phase={voiceMode.phase}
                onExit={voiceMode.exit}
                onToggleMic={voiceMode.toggleMic}
                getOutputVolume={voiceMode.getOutputVolume}
                ducking={voiceMode.isDucking}
              />
            </ErrorBoundary>
          )}
          {(voiceMode.isActive ? [] : messages).map((msg) => {
              // The legacy onboarding greeting ("…Pick how you'd like to get started.")
              // was removed in favor of the welcome card. Hide any such row still
              // persisted in older conversations so onboarding never double-greets.
              if (msg.role !== 'user' && typeof msg.content === 'string' && msg.content.includes("Pick how you'd like to get started")) {
                return null
              }
              const isUser = msg.role === 'user'
              const isMsgStreaming = (msg as any).streaming
              const rawContent = isUser ? msg.content : msg.content

              // Retry bubble (layer 3 of the "No response" recovery): the
              // agent produced a truly empty turn. Show an error bubble with
              // the user's original prompt + a Retry button that re-sends it
              // and a Dismiss × that clears the bubble. Never persisted —
              // refresh or a new user message clears it.
              const retryPrompt = (msg as any).retryPrompt as string | undefined
              // Diagnostic tag for the empty-turn case: 'empty-response' (stream
              // + fallback both returned nothing) vs 'connection-lost' (both threw).
              const retryReason = (msg as any).routing as string | undefined
              if (!isUser && msg.deliveryStatus === 'failed' && retryPrompt) {
                return (
                  <div key={msg.id} className="chat-message-row assistant">
                    <div
                      className="chat-bubble-ai chat-bubble-retry"
                      style={{
                        background: '#fef2f2',
                        borderColor: '#fecaca',
                        color: '#7f1d1d',
                      }}
                    >
                      <div style={{ fontSize: 13, marginBottom: 8, opacity: 0.85 }}>
                        No response from the agent on your last message:
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          fontStyle: 'italic',
                          marginBottom: 12,
                          color: 'var(--color-text-muted)',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {retryPrompt}
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button
                          type="button"
                          onClick={() => { void retryLastMessage() }}
                          style={{
                            padding: '6px 14px',
                            borderRadius: 6,
                            border: '1px solid #991b1b',
                            background: '#991b1b',
                            color: '#fff',
                            fontSize: 13,
                            fontWeight: 500,
                            cursor: 'pointer',
                          }}
                        >
                          Retry
                        </button>
                        <button
                          type="button"
                          onClick={dismissRetryBubble}
                          aria-label="Dismiss retry"
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#7f1d1d',
                            cursor: 'pointer',
                            fontSize: 18,
                            lineHeight: 1,
                            padding: '0 6px',
                          }}
                        >
                          ×
                        </button>
                      </div>
                      {retryReason && (
                        <div style={{ fontSize: 11, marginTop: 8, opacity: 0.6, fontFamily: 'monospace' }}>
                          reason: {retryReason}
                        </div>
                      )}
                    </div>
                  </div>
                )
              }

              // Skip empty assistant bubbles — including streaming ones with no content yet.
              // The "Thinking..." indicator below provides sufficient loading feedback.
              if (!isUser && !rawContent?.trim() && !msg.inlineModuleId) return null

              const isAutoSpeak = (msg as any).routing === 'auto-speak'
              const rowClasses = `chat-message-row ${isUser ? 'user' : 'assistant'}${isAutoSpeak ? ' auto-speak' : ''}${msg.inlineModuleId ? ' module-trigger' : ''}`
              // Inline result cards: an assistant turn may carry a trailing
              // RESULT_CARDS_MARKER block (e.g. matched emails). Strip it from
              // the prose we feed AssistantMarkdown and render the cards after.
              // Tolerates non-marker content → cards = [].
              const { cleanContent: assistantCleanContent, cards: resultCards } = isUser
                ? { cleanContent: rawContent, cards: [] }
                : splitResultCards(rawContent ?? '')
              return (
                <Fragment key={msg.id}>
                  <div className={rowClasses}>
                    {isUser ? (
                      <div className="chat-message">
                        <div className="chat-bubble-user" data-testid="user-message">
                          {rawContent && rawContent.trim() && !/^\[(photo|file)\]$/.test(rawContent.trim()) && (
                            // #1457: render @-mention markers as pills (MentionText
                            // also handles plain text); fall back to markdown RichText
                            // when there are no markers.
                            MENTION_MARKER_RE.test(rawContent)
                              ? <MentionText text={rawContent} />
                              : <RichText blocks={parseBlocks(rawContent)} />
                          )}
                          {msg.attachments && msg.attachments.length > 0 && (
                            <div className="chat-attachments-preview">
                              {msg.attachments.map(att => {
                                // Local blob URLs (in-flight preview) stay as-is; server URLs need the
                                // API base prefix (if relative) plus the JWT in a `_t` query param so
                                // <img>/<a> requests authenticate via the supervisor gateway.
                                const isBlob = att.previewUrl?.startsWith('blob:');
                                const serverUrl = att.url
                                  ? (att.url.startsWith('/') ? `${env.apiBaseUrl}${att.url}` : att.url)
                                  : undefined;
                                const authedServerUrl = serverUrl ? appendAuthToken(serverUrl) : undefined;
                                const imgSrc = isBlob ? att.previewUrl : (authedServerUrl || att.previewUrl);
                                return imgSrc && att.fileType?.startsWith('image/') ? (
                                  <div key={att.assetId || att.filename} className="chat-attachment-image">
                                    <img src={imgSrc} className="chat-attachment-image-preview" alt={att.filename} />
                                  </div>
                                ) : authedServerUrl ? (
                                  <a
                                    key={att.assetId || att.filename}
                                    href={authedServerUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="chat-attachment-chip"
                                    download={att.filename}
                                  >
                                    <svg className="chat-attachment-file-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                      <polyline points="14 2 14 8 20 8"/>
                                    </svg>
                                    <span className="chat-attachment-name">{att.filename}</span>
                                  </a>
                                ) : (
                                  <div key={att.assetId || att.filename} className="chat-attachment-chip">
                                    <svg className="chat-attachment-file-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                      <polyline points="14 2 14 8 20 8"/>
                                    </svg>
                                    <span className="chat-attachment-name">{att.filename}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <div className="message-actions">
                          <button
                            type="button"
                            className="message-action-btn"
                            title="Copy message"
                            onClick={() => {
                              navigator.clipboard.writeText(msg.content || '').then(() => {
                                setCopiedMessageId(msg.id);
                                setTimeout(() => setCopiedMessageId((prev) => prev === msg.id ? null : prev), 2000);
                              }).catch(() => showToast('Copy failed', 'error'));
                            }}
                          >
                            {copiedMessageId === msg.id ? (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                            ) : (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                            )}
                          </button>
                          <button
                            type="button"
                            className="message-action-btn"
                            title="Resend message"
                            onClick={() => { void sendMessage(msg.content || '', undefined, undefined, undefined, { hideUserMessage: true }); }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className={`chat-message${resultCards.some((c) => c.kind === 'today' || c.kind === 'briefing' || c.kind === 'events' || c.kind === 'inbox_triage') ? ' chat-message--briefing' : ''}`}>
                        <div
                          className={`chat-bubble-ai${isAutoSpeak ? ' auto-speak' : ''}`}
                          data-testid="assistant-message"
                          data-message-id={msg.id}
                          data-streaming={isMsgStreaming ? 'true' : 'false'}
                        >
                          <AssistantMarkdown
                            content={assistantCleanContent}
                            moduleLabels={clickableModuleLabels}
                            trailingInline={!isMsgStreaming && assistantCleanContent?.trim() ? (
                              <button
                                type="button"
                                className="message-copy-inline-btn"
                                title="Copy message"
                                onClick={() => {
                                  navigator.clipboard.writeText(assistantCleanContent || '').then(() => {
                                    setCopiedMessageId(msg.id);
                                    setTimeout(() => setCopiedMessageId((prev) => prev === msg.id ? null : prev), 2000);
                                  }).catch(() => showToast('Copy failed', 'error'));
                                }}
                              >
                                {copiedMessageId === msg.id ? (
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                ) : (
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                                )}
                              </button>
                            ) : undefined}
                            onModuleLabelClick={(label, occurrenceIndex) => {
                              void openWorkspaceModuleByLabel(label, msg.id, occurrenceIndex);
                            }}
                            onObjectClick={(objectType, objectId) => {
                              if (objectType === 'contact') {
                                setObjectCardPopover({ objectType, objectId });
                                return;
                              }
                              const objectTypeToModuleTypes: Record<string, string[]> = {
                                asset: ['drive', 'file_viewer'],
                                note: ['drive', 'note_grid', 'docs'],
                                // 'doc' (ADR 023, #3787): a migrated Drive doc citation
                                // routes to the same module family as 'note'.
                                doc: ['drive', 'note_grid', 'docs'],
                                task: ['task_list', 'kanban'],
                                event: ['event_list', 'calendar', 'timeline'],
                                place: ['place_list', 'place_card', 'place_detail'],
                                stat: ['health'],
                              };
                              const candidateTypes = objectTypeToModuleTypes[objectType] ?? [];
                              // Conversation modules take precedence over the workspace catalog.
                              const convModule = availableModulesRef.current.find(m => candidateTypes.includes(m.type));
                              const wsModule = workspaceModuleCatalogRef.current.find(m => candidateTypes.includes(m.type));
                              const targetModule = convModule || wsModule;
                              if (targetModule) {
                                // Focus the module and dispatch object selection.
                                // module.focus handles both mounted and unmounted modules
                                // (attaching via ensureModulePanelInConversationOnce if needed).
                                // Dispatch selectObject with the original object type so
                                // the correct listener fires (e.g. 'note' → doc editor).
                                applyUiActions([{
                                  type: 'module.focus',
                                  moduleId: targetModule.id,
                                } as ChatUIAction]);
                                // Select after module focus settles — use the same delay
                                // pattern as the existing module.focus objectId handler (line ~2030)
                                setTimeout(() => selectObject(objectType as any, objectId), 500);
                              } else {
                                selectObject(objectType as any, objectId);
                              }
                            }}
                            onEmailClick={(email) => {
                              // Focus the Gmail module (if present) and dispatch a
                              // compose event that EmailPanel listens for.
                              const emailModule = availableModulesRef.current.find(m => m.type === 'email')
                                ?? workspaceModuleCatalogRef.current.find(m => m.type === 'email');
                              if (emailModule) {
                                applyUiActions([{ type: 'module.focus', moduleId: emailModule.id } as ChatUIAction]);
                                setTimeout(() => {
                                  window.dispatchEvent(new CustomEvent('xavior:compose-email', { detail: { to: email } }));
                                }, 500);
                              } else {
                                window.dispatchEvent(new CustomEvent('xavior:compose-email', { detail: { to: email } }));
                              }
                            }}
                          />
                          {resultCards.length > 0 && (
                            <ErrorBoundary
                              onError={(error) => console.error('[ResultCard] render error caught by boundary:', error)}
                            >
                              <ResultCardRenderer cards={resultCards} onCardAction={onCardAction} messageId={msg.id} />
                            </ErrorBoundary>
                          )}
                          {(msg as any).metadata?.isError && (
                            <button
                              type="button"
                              className="chat-message-report-link"
                              onClick={() => openModal({
                                type: 'bug_report',
                                errorInfo: {
                                  message: (msg as any).metadata.errorInfo?.message || 'Chat error',
                                  stack: (msg as any).metadata.errorInfo?.stack || 'No stack trace available',
                                  componentStack: 'ChatStreamingError',
                                }
                              })}
                            >
                              Report this issue
                            </button>
                          )}

                          {/* Thinking and metadata (AI only, when complete) */}
                          {!isMsgStreaming && msg.thinking && (
                            <details className="message-thinking">
                              <summary>View thinking process & tool calls</summary>
                              {/* Sanitize at the render point so the panel is clean both
                                  live (thinking is accumulated client-side from SSE deltas
                                  and never routed through serverToClientMessage) and on
                                  reload (#4615). Belt-and-suspenders with the message-mapping
                                  persisted-path cover. */}
                              <pre>{stripInternalUUIDs(msg.thinking)}</pre>
                            </details>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {msg.inlineModuleId && !archivedInlineModuleIds.has(msg.inlineModuleId) && (() => {
                    // Skip inline render when the module pane is already showing this module
                    if (isModulePaneVisible && chatPaneModuleId === msg.inlineModuleId) return null;
                    const inlineMod = availableModules.find((m) => m.id === msg.inlineModuleId);
                    if (!inlineMod) return null;
                    return (
                      <div className="chat-inline-module">
                          <ModuleRenderer
                            module={inlineMod}
                            conversationId={activeConversation?.id}
                            onArchived={() => {
                              setArchivedInlineModuleIds((prev) => new Set(prev).add(inlineMod.id));
                            }}
                            onExpand={() => {
                              setChatPaneModuleId(inlineMod.id);
                              setIsModulePaneVisible(true);
                              setModuleEmbedAnchorMessageId(msg.id);
                            }}
                          />
                      </div>
                    );
                  })()}
                </Fragment>
              )
            })}

            {!isStreaming && quickActions.length > 0 && (
              <QuickActionChips
                actions={quickActions}
                onSelect={async (action) => {
                  if (!activeConversation?.id) return;
                  let message = action.message;
                  let sourceJustSynced: 'demo' | 'email' | 'outlook' | null = null;

                  // Contacts CSV import chip — agent emits this when the
                  // user uploads a CSV/VCF. Open ImportCSVModal at the chat
                  // level pre-loaded with the file the user just sent. The
                  // modal POSTs to /bulk/objects which is workspace-scoped;
                  // it doesn't need a Contacts module to be attached to this
                  // conversation. Look the File up from recentlySentFilesRef.
                  if (isImportContactsChip(action)) {
                    // The File lives only in recentlySentFilesRef (never sent to
                    // the agent). If it's gone (reload/navigation/cleared ref),
                    // show an unmistakable re-attach prompt — never imply the
                    // import succeeded (#4689). Resolver keeps the decision pure
                    // + testable.
                    const result = resolveImportCsvChip(recentlySentFilesRef.current);
                    if (result.kind === 'open') {
                      setChatImportCSVFile(result.file);
                      return;
                    }
                    // No CSV/VCF in hand. For the exact `contacts.import-csv`
                    // chip, surface the honest re-attach prompt. For a label-only
                    // heuristic match (the agent dropped `kind`) with no file,
                    // don't hijack a look-alike chip — fall through to the normal
                    // send path below.
                    if (action.kind === 'contacts.import-csv') {
                      showToast(result.message, 'error');
                      return;
                    }
                  }

                  // Chip-driven OAuth: if the chip carries a kind starting with
                  // "oauth.<provider>[.<scope>]", open the consent popup first
                  // and only dispatch the message on a successful grant. This is
                  // the in-chat OAuth flow for onboarding (#1716).
                  if (action.kind && action.kind.startsWith('oauth.')) {
                    if (action.kind.includes('microsoft')) {
                      const { listProviders } = await import('@/lib/api/endpoints/integrations');
                      const providerList = token ? await listProviders(token) : [];
                      const msAvailable = providerList.some((p: { id: string; hasServerCredentials?: boolean }) => p.id === 'microsoft' && p.hasServerCredentials);
                      if (!msAvailable) return;
                    }
                    const [, provider, ...rest] = action.kind.split('.');
                    const scopeKey = rest.join('.');
                    // ADR 017 D4: name the capability; the backend resolves the
                    // scope union. No hardcoded frontend scope map (the old map
                    // had drifted — it requested gmail.readonly where the real
                    // grant is gmail.modify).
                    const capability = connectCapabilityFor(provider, scopeKey);
                    // Show the Xavior-side consent interstitial first so
                    // users see the read-only / no-send guarantee before
                    // being handed off to Google. Cancel = no popup.
                    const consentProvider = resolveConsentProvider(action.kind);
                    if (consentProvider) {
                      const proceed = await confirmOAuthConsent(consentProvider);
                      if (!proceed) return;
                    }
                    // Pre-redirect side effects (the page unloads on connect):
                    // dismiss the email welcome so the reload lands in the inbox,
                    // and signal any mounted EmailPanel. FLOW A (onboarding first
                    // connect, no integrationId → the backend keeps the picker).
                    // Same-window redirect; on return the reload refreshes session +
                    // onboarding state and useOnboardingDiscovery takes over (the
                    // backend advances onboarding on the OAuth callback).
                    try { localStorage.setItem('email-welcome-dismissed', '1'); } catch { /* best-effort */ }
                    window.dispatchEvent(new CustomEvent('xavior:email-welcome-dismiss'));
                    try {
                      await connectProvider({
                        provider,
                        authToken: token,
                        capability,
                        conversationId: activeConversation?.id,
                      });
                    } catch { /* fetch failed before redirect — stay on chat to retry */ }
                    return;
                  }

                  // Demo-seed chip: the "Use demo data" alternative to OAuth.
                  // Loads synthetic email fixtures into the user's per-user DB
                  // so the rest of onboarding (analysis, contacts, spending)
                  // has something to talk about.
                  // RESILIENCE: retry up to 3× with backoff (api may be
                  // bouncing during dev hot-reloads). Surface a toast on
                  // final failure instead of silently swallowing.
                  if (action.kind === 'demo.load') {
                    let loaded = false;
                    let lastErr: unknown = null;
                    for (let attempt = 1; attempt <= 3; attempt++) {
                      try {
                        await api.demoSeed.loadDemoSeed(token);
                        loaded = true;
                        break;
                      } catch (err) {
                        lastErr = err;
                        console.warn(`[ChatBox] demo-seed load attempt ${attempt} threw:`, err);
                      }
                      if (attempt < 3) await new Promise((r) => setTimeout(r, attempt * 600));
                    }
                    if (!loaded) {
                      console.error('[ChatBox] demo-seed load failed after 3 attempts:', lastErr);
                      showToast('Demo load failed — tap the chip again to retry.', 'error');
                      return;
                    }
                    sourceJustSynced = 'demo';
                    // Skip the Gmail welcome screen so the user lands in
                    // the inbox immediately. Without this they get stuck
                    // on a "Gmail connected — Get started" splash they
                    // didn't ask for and never click. Same shortcut the
                    // real-OAuth flow uses on the calendar_connected URL.
                    try { localStorage.setItem('email-welcome-dismissed', '1'); } catch { /* best-effort */ }
                    // Notify any already-mounted EmailPanel — localStorage
                    // updates don't fire storage events in the same tab,
                    // so the panel needs an explicit signal to drop its
                    // showWelcome state without a page refresh.
                    window.dispatchEvent(new CustomEvent('xavior:email-welcome-dismiss'));
                  }

                  // After a fresh connection (demo OR Gmail), the cached
                  // gmail-status response says "not connected" and the
                  // cached-inbox query has 0 rows. Nuke every gmail-scoped
                  // query so the Email module re-fetches and renders the
                  // freshly-inserted data.
                  if (sourceJustSynced) {
                    queryClient.invalidateQueries({
                      predicate: (q) => {
                        const key = JSON.stringify(q.queryKey);
                        return key.includes('email') || key.includes('gmail') || key.includes('integration');
                      },
                    });
                    if (sourceJustSynced === 'demo') {
                      queryClient.invalidateQueries({ queryKey: ['objects'] });
                      queryClient.invalidateQueries({ queryKey: ['google-accounts'] });
                      queryClient.invalidateQueries({ queryKey: ['modules'] });
                      // Delay the demo-data-loaded event so the EmailPanel has
                      // time to mount after the gmail status query refetches
                      // (connected: false → true). Without this, the event fires
                      // before the panel exists and it never gets the signal.
                      setTimeout(() => {
                        window.dispatchEvent(new CustomEvent('xavior:demo-data-loaded'));
                      }, 1500);
                    }
                  }

                  // "Switch to real Gmail" chip — wipes ALL demo data
                  // (emails, threads, integration row, demo-extracted
                  // contacts) then opens the real Gmail OAuth popup.
                  // No confirmation dialog: the user explicitly tapped
                  // the chip, no surprise. The Email-module header has a
                  // separate confirm-gated path for the same action.
                  let switchedToReal = false;
                  if (action.kind === 'demo.switch-to-real') {
                    try {
                      const result = await api.demoSeed.switchDemoToReal(token);
                      switchedToReal = true;
                      // Nuke all gmail/contact/integration React Query
                      // entries so panels show empty + reflect the
                      // disconnected state immediately.
                      queryClient.invalidateQueries({
                        predicate: (q) => {
                          const k = JSON.stringify(q.queryKey);
                          return k.includes('email') || k.includes('gmail') || k.includes('integration') || k.includes('contact') || k.includes('object');
                        },
                      });
                      window.dispatchEvent(new CustomEvent('xavior:demo-data-loaded'));
                      // Same Xavior-side consent interstitial as the
                      // first-time connect path. User can back out
                      // without ever seeing the Google screen.
                      const proceed = await confirmOAuthConsent('google.email');
                      if (proceed) {
                        // Connect real Gmail via the one connect helper — a
                        // same-window redirect (FLOW A: enroll, no integrationId →
                        // the backend keeps the account picker). On success the page
                        // unloads, so the agent narration can't run here; the reload
                        // reflects the freshly-connected real account.
                        try {
                          await connectProvider({ provider: 'google', authToken: token, capability: 'mail' });
                          return; // redirect in flight — nothing else runs
                        } catch {
                          message = `✓ Demo wiped (${result.messagesRemoved} emails, ${result.contactsRemoved} contacts). Couldn't start the Gmail connect — acknowledge in one sentence and offer to retry.`;
                        }
                      } else {
                        message = `✓ Demo wiped (${result.messagesRemoved} emails, ${result.contactsRemoved} contacts). User cancelled the Gmail connect prompt — acknowledge in one sentence and offer to retry when ready.`;
                      }
                    } catch (err) {
                      console.error('[ChatBox] switch-to-real error:', err);
                      return;
                    }
                  }

                  // Tour chips: open the matching module pane so the
                  // user sees the feature alongside the agent narration.
                  //
                  // #2565 — Onboarding "show calendar" / "show drive" / etc.
                  // chips previously awaited the module-list fetch *and* the
                  // conversation-panel insert *before* dispatching the chat
                  // message, so the agent didn't even start thinking until
                  // both round-trips completed. For tour chips we know
                  // `message` is not rewritten further below (no
                  // sourceJustSynced / contactsExtracted / switchedToReal
                  // mutations apply), so fire `sendMessage` synchronously
                  // and let the module-open work race it in the background.
                  const tourModuleTypes: Record<string, string> = {
                    'calendar.tour': 'event_list',
                    'drive.tour': 'drive',
                    'tasks.tour': 'task_list',
                    'places.tour': 'places',
                    'pages.tour': 'pages',
                  };
                  const isTourChip = action.kind && tourModuleTypes[action.kind];
                  if (isTourChip) {
                    // Fire-and-forget the chat send so latency overlaps with
                    // the module open below. `message` is final at this point
                    // for tour chips, and `opts` is unconditionally undefined
                    // (none of sourceJustSynced/contactsExtracted/switchedToReal
                    // are set on this code path).
                    void sendMessage(message, activeConversation.id, undefined, undefined, undefined);

                    // #2565 — Open the pane synchronously from the in-memory
                    // module catalog (refreshed on mount + on module changes)
                    // so the visual pane open isn't gated on a network
                    // round-trip. The persistence call below runs in the
                    // background — failure is a 409 (panel already exists)
                    // or transient that the next mount recovers from.
                    const targetType = tourModuleTypes[action.kind!];
                    const findMod = (mods: WorkspaceModule[]) => mods.find((m) => m.type === targetType);
                    const openPane = (mod: WorkspaceModule) => {
                      setChatPaneModuleId(mod.id);
                      setIsModulePaneVisible(true);
                      void api.workspaces.createConversationPanel(token, workspaceId, activeConversation.id, {
                        workspaceModuleId: mod.id,
                        panelKey: `tour-${mod.type}`,
                      }).catch(() => { /* 409 = already exists, fine */ });
                    };

                    const cached = findMod(workspaceModuleCatalogRef.current);
                    if (cached) {
                      openPane(cached);
                    } else {
                      // Cache miss (rare during onboarding — catalog is
                      // populated on mount). Fetch once, then open.
                      try {
                        const modules = await api.workspaces.listWorkspaceModules(token, workspaceId, {});
                        const mod = findMod(modules);
                        if (mod) openPane(mod);
                      } catch { /* best effort */ }
                    }
                    // We already sent the message above — skip the trailing
                    // sendMessage at the bottom of this handler.
                    return;
                  }

                  if (action.kind === 'tour.end') {
                    setChatPaneModuleId(null);
                    setIsModulePaneVisible(false);
                  }

                  // Daily-briefing opt-in chip (emitted by the xavior-letter
                  // skill step 5). Flips the per-user daily_briefing_enabled
                  // flag server-side, then forwards the message to the agent
                  // so it can acknowledge. Errors are non-fatal — the agent
                  // still replies, the flag just stays unset.
                  if (action.kind === 'briefing.enable' || action.kind === 'briefing.disable') {
                    const enabled = action.kind === 'briefing.enable';
                    try {
                      await api.onboarding.setBriefingPreference(token, enabled);
                    } catch (err) {
                      console.warn('[ChatBox] briefing-preference failed:', err);
                    }
                  }

                  // After switch-to-real, also need to update the message
                  // with summary IF the OAuth completed and synced data.
                  if (switchedToReal) { /* message already set above */ }

                  // After a successful sync (real Gmail OR demo), rewrite the
                  // outbound user message so the agent narrates the next step.
                  //
                  // CRITICAL: we MUST rewrite the message. Otherwise it stays
                  // as "Use demo data" — a phrase the agent's "re-loading demo
                  // on demand" path matches and responds to by re-emitting the
                  // demo chip + a "Loading demo data — give it a few seconds."
                  // filler, which the user can then click again, looping
                  // forever. The "Narrate this back to me" suffix routes the
                  // agent to its post-sync narration path.
                  if (sourceJustSynced) {
                    const sourceLabel = sourceJustSynced === 'demo' ? 'demo data' : sourceJustSynced === 'outlook' ? 'Outlook' : 'Gmail';
                    message = `✓ Connected (${sourceLabel}). Narrate this back to me as the next step of onboarding.`;
                  }

                  // Instant module focus for Gmail-related actions (don't wait for agent)
                  if (/connect.*email|let.*s do it/i.test(action.message)) {
                    try {
                      const modules = await api.workspaces.listWorkspaceModules(token, workspaceId, {});
                      const emailModule = modules.find((m: any) => m.type === 'email');
                      if (emailModule) {
                        setChatPaneModuleId(emailModule.id);
                        setIsModulePaneVisible(true);
                      }
                    } catch { /* best effort */ }
                  }
                  // After sync, contact extraction, or switch-to-real,
                  // we inject the result into the user message so the
                  // agent has real numbers to narrate. Show the chip's
                  // label as the visible bubble. Fall back to action.label
                  // because BOOTSTRAP-emitted chips (e.g. demo.load) only
                  // carry {label, kind} — without this fallback the long
                  // analysis blob leaks into the user bubble.
                  const opts = (sourceJustSynced || switchedToReal)
                    ? { displayContent: action.message ?? action.label }
                    : undefined;
                  void sendMessage(message, activeConversation.id, undefined, undefined, opts);
                }}
              />
            )}

            {/* Live streaming activity status pill — stays mounted for the whole
                streaming turn (never blanks mid-turn), so it co-displays below the
                answer while text is streaming. */}
            {shouldShowThinkingTimeline && !voiceMode.isActive && (
              <ThinkingTimeline steps={thinkingTimelineSteps} />
            )}

            {pendingModuleSelection && (
              <ModuleSelectionButtons
                modules={pendingModuleSelection.modules}
                onSelect={handleModuleSelection}
                onCancel={() => setPendingModuleSelection(null)}
              />
            )}

            {/* Inline error card — rendered inside the chat stream so the
                max-iterations / network / agent error appears as a
                system-error message rather than as a free-floating bar
                wedged between the last message and the composer (#1480). */}
            {error && (
              <ChatInlineError
                error={error}
                onRetry={retryLastMessage}
                onReport={(err) => openModal({
                  type: 'bug_report',
                  errorInfo: {
                    message: err.message,
                    stack: err.stack,
                    componentStack: 'ChatBox',
                  }
                })}
              />
            )}

          {/* Visually-hidden polite live region — announces the assistant's
              answer once streaming finishes (#5263). Empty while streaming. */}
          <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
            {liveAnnouncement}
          </div>

          <div ref={messagesEndRef} />
        </div>
  );
});
